import { useState, FormEvent } from 'react';
import { useAction } from 'convex/react';
import { api } from '../../convex/_generated/api';
import './ForgotPassword.css';

interface ForgotPasswordProps {
  onBack: () => void;
}

export default function ForgotPassword({ onBack }: ForgotPasswordProps) {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const requestReset = useAction(api.auth.requestPasswordReset);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await requestReset({ email });
      setSuccess(true);
    } catch (err: any) {
      // Clean up technical error details
      const errorMessage = err.message || 'Failed to send reset email';
      const cleanMessage = errorMessage
        .split('\n')[0]
        .replace(/\[CONVEX.*?\]/g, '')
        .replace(/at handler.*$/g, '')
        .replace(/Called by client.*$/g, '')
        .replace(/Uncaught Error:\s*/g, '')
        .replace(/Server Error\s*/g, '')
        .trim();

      setError(cleanMessage || 'Failed to send reset email');
    } finally {
      setIsLoading(false);
    }
  };

  if (success) {
    return (
      <div className="forgot-password-container">
        <div className="forgot-password-card">
          <div className="success-icon">✅</div>
          <h2>Check Your Email</h2>
          <p>
            If an account exists with <strong>{email}</strong>, you'll receive a password
            reset link shortly.
          </p>
          <p className="helper-text">
            The link will expire in 1 hour. Check your spam folder if you don't see it.
          </p>
          <button className="btn-back" onClick={onBack}>
            ← Back to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="forgot-password-container">
      <div className="forgot-password-card">
        <div className="forgot-password-header">
          <h2>🔒 Reset Password</h2>
          <p>Enter your email address and we'll send you a reset link</p>
        </div>

        <form onSubmit={handleSubmit} className="forgot-password-form">
          {error && (
            <div className="forgot-password-error">
              ⚠️ {error}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="email">Email Address</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              placeholder="your.email@company.com"
              disabled={isLoading}
              autoFocus
            />
          </div>

          <button
            type="submit"
            className="btn-submit"
            disabled={isLoading}
          >
            {isLoading ? 'Sending...' : 'Send Reset Link'}
          </button>

          <button
            type="button"
            className="btn-back"
            onClick={onBack}
            disabled={isLoading}
          >
            ← Back to Login
          </button>
        </form>
      </div>
    </div>
  );
}
